import React, { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Loader2, Lock, MoreHorizontal, RefreshCw, Search } from 'lucide-react';
import { useAuth } from '../../../hooks/useAuth';
import { formatEuro } from '../../../utils/formatEuro';
import { LibraryApiError, isCentralLockError, roundMoney } from './api';

/**
 * Κοινά κομμάτια των τριών βιβλιοθηκών (Ομάδες επιλογών / μεγεθών / Υλικά), στην εμφάνιση του mock
 * «Κατάλογος» (12/10/2026): πάνελ με κεφαλίδα, κλείδωμα κεντρικού καταλόγου, πεδία ποσού, διακόπτης,
 * μενού γραμμής και η λωρίδα «Έχεις αλλαγές…» μέσα στην ανοιχτή κάρτα.
 */

/* ───────────────────────────── κλάσεις ───────────────────────────── */

export const FIELD =
  'w-full min-w-0 h-9 px-[11px] rounded-[9px] border border-line-strong bg-white dark:bg-surface-2 text-[13.5px] text-t1 ' +
  'placeholder:text-t4 focus:outline-none focus:border-brand focus:ring-[3px] focus:ring-brand/15 disabled:opacity-60';
export const FIELD_SM =
  'w-full min-w-0 h-8 px-2 rounded-lg border border-line-strong bg-white dark:bg-surface-2 text-[13px] text-t1 ' +
  'placeholder:text-t4 focus:outline-none focus:border-brand focus:ring-[3px] focus:ring-brand/15 disabled:opacity-60';
/** Πεδίο που άλλαξε και δεν αποθηκεύτηκε (κίτρινο, όπως στο mock). */
export const CHANGED = '!border-amber-500 !bg-amber-100/70 dark:!bg-amber-500/15 font-semibold';
export const INVALID = '!border-red-500 focus:!ring-red-500/20';
export const BTN =
  'inline-flex items-center justify-center gap-1.5 h-9 px-3.5 rounded-[10px] border border-line-strong bg-surface-2 ' +
  'text-[13.5px] font-semibold text-t1 whitespace-nowrap hover:bg-surface-3 transition-colors disabled:opacity-50 disabled:cursor-not-allowed';
export const BTN_SM = 'inline-flex items-center justify-center gap-1.5 h-8 px-[11px] rounded-[9px] text-[13px] font-semibold whitespace-nowrap transition-colors disabled:opacity-50 disabled:cursor-not-allowed';
export const BTN_PRIMARY =
  'inline-flex items-center justify-center gap-1.5 h-9 px-3.5 rounded-[10px] bg-brand text-brand-fg text-[13.5px] font-semibold ' +
  'whitespace-nowrap hover:brightness-110 transition disabled:opacity-50 disabled:cursor-not-allowed';
export const BTN_GHOST = `${BTN_SM} text-t2 hover:bg-surface-3/70 hover:text-t1`;
export const ICON_BTN =
  'inline-grid place-items-center w-7 h-7 shrink-0 rounded-lg text-t3 hover:bg-surface-3 hover:text-t1 transition-colors ' +
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand disabled:opacity-40';
export const LABEL = 'block text-[12.5px] font-semibold text-t2';
export const HINT = 'text-xs leading-snug text-t3';
export const BADGE = 'inline-flex items-center h-5 px-[7px] rounded-md text-[11.5px] font-semibold whitespace-nowrap bg-surface-3 text-t2';
export const BADGE_OUTLINE = 'inline-flex items-center h-5 px-[7px] rounded-md text-[11.5px] font-semibold whitespace-nowrap ring-1 ring-inset ring-line-strong text-t2';
export const BADGE_BRAND = 'inline-flex items-center h-5 px-[7px] rounded-md text-[11.5px] font-semibold whitespace-nowrap bg-brand-soft text-brand';
/** Κεφαλίδα στηλών λίστας (ΟΜΑΔΑ · ΕΠΙΛΟΓΕΣ · ΣΕ ΠΡΟΪΟΝΤΑ). */
export const COLS_HEAD = 'bg-surface-1 dark:bg-surface-0 border-b border-line text-[11px] font-semibold uppercase tracking-[0.05em] text-t4';
/** Το «πηγάδι» της ανοιχτής γραμμής, με τη μπλε λωρίδα αριστερά. */
export const OPEN_ROW = 'bg-surface-1 dark:bg-surface-0 shadow-[inset_3px_0_0_hsl(var(--brand))]';

/* ───────────────────────────── βοηθητικά ───────────────────────────── */

/** «ζαχαρη» βρίσκει τη «Ζάχαρη»: χωρίς τόνους και κεφαλαία. */
export const searchKey = (s: string): string =>
  s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLocaleLowerCase('el');

const AMOUNT = new Intl.NumberFormat('el-GR', { minimumFractionDigits: 2, maximumFractionDigits: 2, useGrouping: false });
/** «0,50» — για πεδία (το € είναι δίπλα). */
export const formatAmount = (n: number): string => AMOUNT.format(n);
/** «2,60 €» — για ανάγνωση. */
export const euro = (n: number): string => formatEuro(n);

/** «0,5», «0.50», «3» → αριθμός με 2 δεκαδικά· κενό ή άκυρο → null. */
export function parseAmount(text: string): number | null {
  const s = text.trim().replace(/\s+/g, '').replace(',', '.');
  if (!/^-?(\d+(\.\d{0,2})?|\.\d{1,2})$/.test(s)) return null;
  const n = Number(s);
  return Number.isFinite(n) ? roundMoney(n) : null;
}

/** Ίδιο όνομα για τη βάση (UNIQUE) — ακριβώς, μετά το trim. */
export const sameName = (a: string, b: string) => a.trim() === b.trim();
/** Ίδιο όνομα για τον άνθρωπο (χωρίς τόνους/κεφαλαία) — για το «υπάρχει ήδη». */
export const similarName = (a: string, b: string) => searchKey(a.trim()) === searchKey(b.trim());

/**
 * Κεντρικός κατάλογος: μόνο ανάγνωση (όπως στα «Προϊόντα»). Αν ο server απαντήσει CATALOG_CENTRAL
 * σε κατάστημα που το token λέει ανεξάρτητο, κλειδώνει κι εδώ ως την επόμενη φόρτωση.
 */
export function useCatalogLock(): { locked: boolean; noteError: (err: unknown) => void } {
  const { store } = useAuth();
  const [serverLocked, setServerLocked] = useState(false);
  return {
    locked: store?.catalog_management === 'central' || serverLocked,
    noteError: (err: unknown) => { if (isCentralLockError(err)) setServerLocked(true); },
  };
}

/** Μήνυμα αποτυχίας για τον χειριστή (το LibraryApiError το έχει ήδη έτοιμο, βλ. api.ts). */
export function useLibraryErrorText(): (err: unknown) => string {
  const { t } = useTranslation(['products']);
  return (err: unknown) => (err instanceof LibraryApiError && err.message ? err.message : t('products:libraries.errors.generic'));
}

/* ───────────────────────────── πάνελ ───────────────────────────── */

export const LibraryPane: React.FC<{
  title: string;
  count?: number | null;
  hint: React.ReactNode;
  /** Αναζήτηση, «Νέα ομάδα» κ.λπ. */
  actions?: React.ReactNode;
  locked: boolean;
  testId?: string;
  children: React.ReactNode;
}> = ({ title, count, hint, actions, locked, testId, children }) => {
  const titleId = useId();
  return (
    <section
      aria-labelledby={titleId}
      data-testid={testId}
      className="flex flex-col flex-1 min-h-0 min-w-0 rounded-2xl border border-line bg-surface-2 dark:bg-surface-1 shadow-[0_1px_2px_rgb(29_45_70_/_4%)] overflow-hidden"
    >
      <header className="flex flex-wrap items-center gap-3 pl-[18px] pr-3.5 pt-3.5 pb-3 border-b border-line">
        <div className="flex-1 min-w-[14rem]">
          <h2 id={titleId} className="flex items-center gap-2 text-lg font-bold leading-tight tracking-[-0.02em] text-t1">
            {title}
            {count != null && (
              <span className="px-2 py-px rounded-full bg-surface-3 text-xs font-semibold tracking-normal tabular-nums text-t3">{count}</span>
            )}
          </h2>
          <p className={`mt-0.5 ${HINT}`}>{hint}</p>
        </div>
        {actions}
      </header>
      {locked && <CentralBanner />}
      {children}
    </section>
  );
};

export const CentralBanner: React.FC = () => {
  const { t } = useTranslation(['products']);
  return (
    <div role="note" className="mx-[18px] mt-3 flex items-start gap-2.5 rounded-[10px] border border-amber-500/40 bg-amber-500/10 px-3 py-2.5">
      <Lock className="mt-0.5 h-4 w-4 shrink-0 text-amber-700 dark:text-amber-400" aria-hidden="true" />
      <div className="text-[12.5px]">
        <p className="font-semibold text-amber-900 dark:text-amber-200">{t('products:libraries.centralTitle')}</p>
        <p className="text-amber-900/80 dark:text-amber-200/80">{t('products:libraries.centralHint')}</p>
      </div>
    </div>
  );
};

export const SearchField: React.FC<{ value: string; onChange: (v: string) => void; placeholder: string }> = ({ value, onChange, placeholder }) => (
  <label className="relative block flex-[0_1_280px] min-w-[180px]">
    <Search className="pointer-events-none absolute left-2.5 top-1/2 h-[15px] w-[15px] -translate-y-1/2 text-t4" aria-hidden="true" />
    <input
      type="search"
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      aria-label={placeholder}
      className={`${FIELD} pl-8`}
    />
  </label>
);

export const LoadState: React.FC<{ state: 'loading' | 'error'; onRetry: () => void }> = ({ state, onRetry }) => {
  const { t } = useTranslation(['products']);
  if (state === 'loading') {
    return (
      <div role="status" className="flex items-center justify-center gap-2 py-16 text-sm text-t3">
        <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
        {t('products:libraries.loading')}
      </div>
    );
  }
  return (
    <div role="alert" className="flex flex-col items-center gap-3 py-14 text-center">
      <AlertTriangle className="h-6 w-6 text-red-600 dark:text-red-400" aria-hidden="true" />
      <p className="text-sm text-t2">{t('products:libraries.loadError')}</p>
      <button type="button" onClick={onRetry} className={BTN}>
        <RefreshCw className="h-[15px] w-[15px]" aria-hidden="true" />
        {t('products:libraries.retry')}
      </button>
    </div>
  );
};

export const EmptyState: React.FC<{ title: string; hint?: string; action?: React.ReactNode }> = ({ title, hint, action }) => (
  <div className="flex flex-col items-center gap-1.5 px-6 py-12 text-center text-[13px] text-t3">
    <b className="text-sm font-semibold text-t1">{title}</b>
    {hint && <span>{hint}</span>}
    {action && <div className="mt-2">{action}</div>}
  </div>
);

/** Πόσα προϊόντα· «—» όταν ο server δεν το στέλνει ακόμη. */
export const UsageCount: React.FC<{ count: number | null; className?: string }> = ({ count, className = '' }) => {
  const { t } = useTranslation(['products']);
  if (count == null) {
    return <span className={`text-t4 ${className}`} title={t('products:libraries.usageUnknown')}>—</span>;
  }
  return <span className={`tabular-nums ${className}`}>{count}</span>;
};

/* ───────────────────────────── πεδία ───────────────────────────── */

/**
 * Ποσό σε € με κόμμα ή τελεία. Όσο γράφεις κρατά ό,τι πληκτρολόγησες· η τιμή φεύγει προς τα πάνω σε
 * κάθε πλήκτρο (αριθμός ή null = κενό/άκυρο), ώστε η αλλαγή να φαίνεται αμέσως.
 */
export const MoneyInput: React.FC<{
  value: number | null;
  onChange: (value: number | null) => void;
  label: string;
  id?: string;
  changed?: boolean;
  invalid?: boolean;
  disabled?: boolean;
  size?: 'sm' | 'md';
  className?: string;
}> = ({ value, onChange, label, id, changed = false, invalid = false, disabled = false, size = 'sm', className = '' }) => {
  const [focused, setFocused] = useState(false);
  const [text, setText] = useState(() => (value == null ? '' : formatAmount(value)));
  useEffect(() => {
    if (!focused) setText(value == null ? '' : formatAmount(value));
  }, [value, focused]);
  return (
    <div className={`relative min-w-0 ${className}`}>
      <input
        id={id}
        type="text"
        inputMode="decimal"
        autoComplete="off"
        aria-label={label}
        aria-invalid={invalid || undefined}
        value={text}
        disabled={disabled}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onChange={e => { setText(e.target.value); onChange(parseAmount(e.target.value)); }}
        className={`${size === 'sm' ? FIELD_SM : FIELD} pr-6 text-right tabular-nums ${changed ? CHANGED : ''} ${invalid ? INVALID : ''}`}
      />
      <span aria-hidden="true" className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-xs text-t4">€</span>
    </div>
  );
};

/** Διακόπτης ναι/όχι (role="switch"). */
export const Toggle: React.FC<{
  checked: boolean;
  onChange?: (next: boolean) => void;
  label?: string;
  labelledBy?: string;
  disabled?: boolean;
  changed?: boolean;
}> = ({ checked, onChange, label, labelledBy, disabled = false, changed = false }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={label}
    aria-labelledby={labelledBy}
    disabled={disabled}
    onClick={() => onChange?.(!checked)}
    className={`relative inline-block h-[19px] w-8 shrink-0 rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-surface-2 disabled:cursor-not-allowed disabled:opacity-60 ${
      checked ? 'bg-brand' : 'bg-surface-4'
    } ${changed ? 'ring-2 ring-amber-500 ring-offset-2 ring-offset-surface-2' : ''}`}
  >
    <span
      aria-hidden="true"
      className={`absolute left-[2.5px] top-[2.5px] h-[14px] w-[14px] rounded-full bg-white shadow-[0_1px_2px_rgb(0_0_0_/_25%)] transition-transform ${checked ? 'translate-x-[13px]' : ''}`}
    />
  </button>
);

/** Δύο-τρεις επιλογές σε ένα κουτί (Μία / Πολλές). */
export function Segmented<V extends string>({ value, options, onChange, label, changed = false }: {
  value: V;
  options: Array<{ value: V; label: string }>;
  onChange: (next: V) => void;
  label: string;
  changed?: boolean;
}) {
  return (
    <div
      role="radiogroup"
      aria-label={label}
      className={`inline-flex gap-0.5 rounded-[10px] border p-0.5 bg-surface-2 ${changed ? 'border-amber-500' : 'border-line'}`}
    >
      {options.map(o => {
        const on = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={on}
            onClick={() => onChange(o.value)}
            className={`h-7 rounded-lg px-3.5 text-[13px] transition-colors ${on ? 'bg-brand-soft font-semibold text-brand' : 'font-medium text-t2 hover:bg-surface-3/70 hover:text-t1'}`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

/* ───────────────────────────── μενού γραμμής ───────────────────────────── */

export interface RowMenuItem { label: string; onSelect: () => void; danger?: boolean; icon?: React.ElementType }

/** «⋯» στο τέλος της γραμμής (Αντίγραφο · Διαγραφή). */
export const RowMenu: React.FC<{ label: string; items: RowMenuItem[] }> = ({ label, items }) => {
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);
  const menuId = useId();
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: MouseEvent) => { if (!boxRef.current?.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    boxRef.current?.querySelector<HTMLButtonElement>('[role="menuitem"]')?.focus();
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);
  return (
    <div ref={boxRef} className="relative">
      <button
        type="button"
        aria-label={label}
        title={label}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => setOpen(o => !o)}
        className={`${ICON_BTN} w-8 h-8`}
      >
        <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
      </button>
      {open && (
        <div
          id={menuId}
          role="menu"
          aria-label={label}
          className="absolute right-0 top-full z-20 mt-1 min-w-[170px] rounded-xl border border-line bg-surface-2 p-1 shadow-[0_16px_40px_-12px_rgb(29_45_70_/_30%),0_2px_6px_rgb(29_45_70_/_8%)]"
        >
          {items.map(item => {
            const Icon = item.icon;
            return (
              <button
                key={item.label}
                type="button"
                role="menuitem"
                onClick={() => { setOpen(false); item.onSelect(); }}
                className={`flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-[13px] font-medium hover:bg-surface-3 focus:bg-surface-3 focus:outline-none ${
                  item.danger ? 'text-red-700 dark:text-red-300' : 'text-t1'
                }`}
              >
                {Icon && <Icon className="h-[15px] w-[15px]" aria-hidden="true" />}
                {item.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

/* ───────────────────────────── «Έχεις αλλαγές…» ───────────────────────────── */

/**
 * Η αποθήκευση της ανοιχτής κάρτας (ίδια λόγια με τη μπάρα των ρυθμίσεων). Μέσα στην κάρτα και όχι
 * στο κάτω μέρος της οθόνης: η σελίδα «Κατάλογος» έχει δική της μπάρα για τα προϊόντα.
 * Κολλάει στο κάτω μέρος της λίστας όσο η κάρτα φαίνεται.
 */
export const CardSaveBar: React.FC<{
  dirty: boolean;
  saving: boolean;
  canSave: boolean;
  onSave: () => void;
  onReset: () => void;
  /** Νέα εγγραφή: «Ακύρωση» αντί για «Αναίρεση», και φαίνεται πάντα. */
  isNew?: boolean;
  /** Κάρτα που κλείνει μόνο από εδώ: χωρίς αλλαγές δείχνει «Κλείσιμο». */
  onClose?: () => void;
  error?: string | null;
  /** Αριστερά (π.χ. «Διαγραφή ομάδας»). */
  leading?: React.ReactNode;
}> = ({ dirty, saving, canSave, onSave, onReset, isNew = false, onClose, error, leading }) => {
  const { t } = useTranslation(['settings', 'common']);
  const pending = dirty || isNew;
  if (!pending && !error && !leading && !onClose) return null;
  return (
    <div className="sticky bottom-2 z-10 mt-1 space-y-2">
      {error && (
        <p role="alert" className="rounded-[10px] border border-red-500/30 bg-red-500/10 px-3 py-2 text-[12.5px] text-red-800 dark:text-red-200">
          {error}
        </p>
      )}
      <div
        role="region"
        aria-label={t('settings:unsaved.region')}
        data-testid={pending ? 'card-save-bar' : undefined}
        className={`flex flex-wrap items-center gap-x-3 gap-y-2 rounded-xl px-3 py-2 ${
          dirty ? 'border border-amber-500/40 bg-amber-50 dark:bg-amber-500/10 shadow-[0_8px_24px_-12px_rgb(0_0_0_/_25%)]' : ''
        }`}
      >
        {leading}
        <span className="flex min-w-[12rem] flex-1 items-center gap-2 text-[13px] font-medium text-t1">
          {dirty && <span aria-hidden="true" className="h-2 w-2 shrink-0 rounded-full bg-amber-500 shadow-[0_0_0_3px_rgb(245_158_11_/_25%)]" />}
          {dirty ? t('settings:unsaved.message') : ''}
        </span>
        <div className="ml-auto flex items-center gap-2">
          {pending ? (
            <>
              <button type="button" onClick={onReset} disabled={saving} className={`${BTN} h-8`}>
                {isNew ? t('common:cancel') : t('settings:unsaved.reset')}
              </button>
              <button type="button" onClick={onSave} disabled={saving || !canSave} className={`${BTN_PRIMARY} h-8`}>
                {saving && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
                {saving ? t('common:saving') : t('settings:unsaved.save')}
              </button>
            </>
          ) : onClose && (
            <button type="button" onClick={onClose} className={`${BTN} h-8`}>{t('common:close')}</button>
          )}
        </div>
      </div>
    </div>
  );
};
